import { Box, Typography, useTheme } from "@mui/material"
import { decomposeColor } from "@mui/material/styles"
import { useEffect, useRef } from "react"
import { useParams } from "react-router-dom"
import { If } from "lib/@components/switch"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useSearchParam, useSearchParamsBatch } from "lib/@hooks/use-search-param"
import { setReference, useReference } from "lib/@hooks/use-reference"
import { useDemand } from "lib/authorization/use-demand"
import { getTreeId } from "routes/schedule/lib/getTreeId"
import { getScheduleRecordId } from "library/get-unique-id-from-tree.mjs"
import { getSharingLink } from "routes/sharing/controller/get-sharing-link"
import {
    SHARE_LINK_COMMENTS_TAB,
    SHARE_LINK_COMMENT_SHARE_PARAM,
    SHARE_LINK_COMMENT_TARGET_REFERENCE,
    buildTaskCommentTarget,
    filterShareLinkCommentHighlights,
    findSelectedShareLinkComment,
    isShareLinkCommentForTarget,
    shouldShowShareLinkCommentsTab,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-presenters"
import {
    SHARE_LINK_COMMENT_PART_ATTRIBUTE,
    SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE,
    SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE,
    clearPartHighlights,
    findCommentPartElement,
    getCommentAnchorPart,
    getCommentPartValue,
    getShareLinkCommentPartLabel,
    markPartHighlight,
    resolveClickedPartCommentId,
} from "routes/schedule/plugins/share-link-comments/share-link-comment-parts"
import {
    getRegimeScheduleComments,
    getShareLinkScheduleComments,
} from "routes/schedule/plugins/share-link-comments/share-link-comments.controller"
import {
    SHARE_LINK_COMMENT_HIGHLIGHT_CLASS,
    SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_DRAFT,
    SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED,
    SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED,
    clearTextHighlights,
    getHighlightFragments,
    getHighlightPartyColorKey,
    highlightText,
    resolveClickedCommentId,
} from "routes/schedule/plugins/share-link-comments/comment-text-highlight"
import { ShareLinkCommentFocus } from "routes/schedule/plugins/share-link-comments/share-link-comment-event-definitions"
import { useScheduleRightBarTab } from "routes/schedule/plugins/share-link-comments/use-schedule-right-bar-tab"

const EMPTY_COMMENTS = []
const HIGHLIGHT_MISSING_ATTRIBUTE = "data-share-link-comment-highlight-missing"
const HIGHLIGHT_ALPHA_SELECTED = 0.16
const HIGHLIGHT_ALPHA_MUTED = 0.06
const PART_HIGHLIGHT_INSET = "-2px -4px"
const BOXED_PART_HIGHLIGHT_HEIGHT = 36
const BOXED_PART_HIGHLIGHT_WIDTH_OFFSET = 4
const BOXED_COMMENT_PARTS = new Set(["classification", "title", "time", "frequency", "uom", "skill"])
// Clearance for the sticky schedule/version header when a comment target is scrolled to the top.
const SCROLL_MARGIN_TOP = 64

export function getShareLinkPartHighlightSx(part) {
    if (BOXED_COMMENT_PARTS.has(part)) {
        return {
            top: "50%",
            ...getShareLinkPartHighlightHorizontalSx(part),
            bottom: "auto",
            height: `${BOXED_PART_HIGHLIGHT_HEIGHT}px`,
            transform: "translateY(-50%)",
        }
    }
    return { inset: PART_HIGHLIGHT_INSET }
}

function getShareLinkPartHighlightHorizontalSx(part) {
    if (part === "uom") {
        return {
            left: `-${BOXED_PART_HIGHLIGHT_WIDTH_OFFSET}px`,
            right: 0,
        }
    }
    if (part === "skill") {
        return {
            left: 0,
            right: `-${BOXED_PART_HIGHLIGHT_WIDTH_OFFSET}px`,
        }
    }
    return {
        left: `-${BOXED_PART_HIGHLIGHT_WIDTH_OFFSET}px`,
        right: `-${BOXED_PART_HIGHLIGHT_WIDTH_OFFSET}px`,
    }
}

function getCommentPartSelector(part, { highlighted }) {
    const stateSelector = highlighted
        ? `[${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}]`
        : `:not([${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}])`
    return `& [${SHARE_LINK_COMMENT_PART_ATTRIBUTE}="${part}"]${stateSelector}`
}

function getBoxedPartPseudoSx({ highlighted }) {
    const sx = {}
    for (const part of BOXED_COMMENT_PARTS) {
        sx[`${getCommentPartSelector(part, { highlighted })}::before`] = getShareLinkPartHighlightSx(part)
    }
    return sx
}

export function ShareLinkSelectedCommentTarget({ children, targetType, schedule: propSchedule }) {
    const ref = useRef()
    const theme = useTheme()
    const setSearchParams = useSearchParamsBatch()
    const { id: routeSchedulePath } = useParams()
    const { code, currentId, isShare, schedule: contextSchedule, schedulePath, task } = useBoundContext()
    const schedule = propSchedule ?? contextSchedule
    const [commentId] = useSearchParam("comment", null)
    const [shareLinkId] = useSearchParam("share", null)
    const [commentShareLinkId] = useSearchParam(SHARE_LINK_COMMENT_SHARE_PARAM, null)
    const rightBarTab = useScheduleRightBarTab()
    // Same reference the comment buttons write and the panel composer reads: while a comment is
    // being composed it holds the draft target (incl. the text the user selected).
    const [rightBarReference] = useReference({ [SHARE_LINK_COMMENT_TARGET_REFERENCE]: null }, "tab-ScheduleRightBar-")
    const commentsLinkId = shareLinkId ?? commentShareLinkId
    const shareLink = getSharingLink.useResults(commentsLinkId)
    // Authoring a part-pinned comment (click a task item like Frequency) needs a comment-enabled
    // share link in context, exactly like the other comment entry points.
    const canComposeParts =
        targetType === "task" &&
        !!task?.id &&
        shouldShowShareLinkCommentsTab({ isShare: !!isShare || !!shareLinkId, shareLinkId: commentsLinkId, shareLink })
    const isShareLinkUser = useDemand("$sharing")
    const isRegimeEditor = useDemand("manageRegime")
    const isInternal = isRegimeEditor || !isShareLinkUser
    const activeSchedulePath = schedulePath ?? currentId ?? routeSchedulePath
    const regimeId = activeSchedulePath ? getTreeId(activeSchedulePath) : undefined
    const scheduleId = getCommentScheduleId()
    const internalComments = getRegimeScheduleComments.useResults({
        regimeId: isInternal ? regimeId : null,
        scheduleId: isInternal ? scheduleId : null,
    })
    const recipientComments = getShareLinkScheduleComments.useResults({
        shareLinkId: isInternal ? null : commentsLinkId,
        regimeId,
        scheduleId,
    })
    const comments = getVisibleComments()
    const selectedComment = findSelectedShareLinkComment(comments, commentId)
    const targetComments = getTargetComments()
    const hasTargetComment = getHasTargetComment()
    const draftTarget = getDraftTarget()
    const hasHighlightTarget = hasTargetComment || !!draftTarget
    const isSelected = getIsSelected()
    const highlightFragments = getHighlightFragmentsForTarget()
    const partHighlights = getPartHighlightsForTarget()
    // Stable key so the highlight effect only re-runs when the texts/owners/parts change.
    const highlightKey = [
        ...highlightFragments.map(
            (fragment) =>
                `${fragment.commentId}:${fragment.text}:${fragment.emphasis}:${fragment.context?.beforeText}:${fragment.context?.afterText}`
        ),
        ...partHighlights.map((mark) => `${mark.commentId}:part:${mark.part}:${mark.emphasis}`),
    ].join("\n")
    const hasChildren = children !== undefined && children !== null

    useEffect(scrollToSelectedTarget, [isSelected, commentId])
    // highlightKey is the stable serialization of highlightFragments; re-running only when it
    // changes avoids re-walking the DOM on every render while still reflecting fragment changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(applyTextHighlights, [hasHighlightTarget, highlightKey])
    // Hover linkage with the panel: when a panel thread card focuses a comment, lift the matching
    // in-document highlight to "selected" emphasis (and restore on blur). We toggle a data attribute
    // on the spans directly rather than re-rendering, keeping the linkage cheap.
    ShareLinkCommentFocus.useEvent(syncHighlightFocus, [highlightKey])

    if (!hasChildren && !hasTargetComment) return null
    if (!hasChildren) {
        return (
            <Box ref={ref} data-cy={getDataCy()} sx={{ scrollMarginTop: `${SCROLL_MARGIN_TOP}px`, ...getMarkerSx() }}>
                <If value={targetComments.length > 1}>
                    <Box sx={getMarkerCountSx()} data-cy={`${getDataCy()}-count`}>
                        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>
                            {targetComments.length}
                        </Typography>
                    </Box>
                </If>
            </Box>
        )
    }

    return (
        <Box
            ref={ref}
            data-cy={getDataCy()}
            sx={{ scrollMarginTop: `${SCROLL_MARGIN_TOP}px`, ...getSx() }}
            onClickCapture={handlePartClickCapture}
            onClick={handleHighlightClick}
            onMouseOver={handleHighlightHover}
            onMouseOut={handleHighlightHoverOut}
        >
            {children}
        </Box>
    )

    function getCommentScheduleId() {
        if (activeSchedulePath) return getScheduleRecordId(activeSchedulePath)
        if (code) return code
        return schedule?.id
    }

    function getCanShowHighlights() {
        if (shareLinkId) return true
        return rightBarTab === SHARE_LINK_COMMENTS_TAB
    }

    function getVisibleComments() {
        if (!getCanShowHighlights()) return EMPTY_COMMENTS
        const source = (isInternal ? internalComments : recipientComments) ?? EMPTY_COMMENTS
        return filterShareLinkCommentHighlights(source, { shareLinkId })
    }

    function getIsSelected() {
        if (!selectedComment) return false
        return isCommentForThisTarget(selectedComment)
    }

    function getHasTargetComment() {
        return targetComments.length > 0
    }

    function getTargetComments() {
        return comments.filter(isCommentForThisTarget)
    }

    function getHighlightFragmentsForTarget() {
        // Draft fragments lead the list so the in-progress selection wins the first occurrence of
        // contested text over a saved comment on the same words.
        const fragments = getDraftHighlightFragments()
        const seen = new Set()
        for (const comment of targetComments) {
            if (!isCommentForThisTarget(comment)) continue
            // Part-pinned comments mark the part's element instead (see getPartHighlightsForTarget).
            if (getCommentAnchorPart(comment)) continue
            const commentId = comment?.id ?? comment?.threadId
            const emphasis = getFragmentEmphasis(comment)
            for (const text of getHighlightFragments(comment?.anchor?.targetText)) {
                const key = `${commentId}:${text}`
                if (seen.has(key)) continue
                seen.add(key)
                fragments.push({ text, commentId, emphasis, context: getCommentHighlightContext(comment) })
            }
        }
        return fragments
    }

    // Comments pinned to a task item (Duration, Frequency, …) highlight the item's ELEMENT rather
    // than matching text — values like "9m" are too short and too ambiguous for the text walker.
    function getPartHighlightsForTarget() {
        const marks = []
        const draftPart = draftTarget?.selectionMetadata?.part
        if (getShareLinkCommentPartLabel(draftPart)) {
            marks.push({ part: draftPart, commentId: undefined, emphasis: SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_DRAFT })
        }
        const seen = new Set()
        for (const comment of targetComments) {
            const part = getCommentAnchorPart(comment)
            if (!part) continue
            const commentId = comment?.id ?? comment?.threadId
            const key = `${commentId}:${part}`
            if (seen.has(key)) continue
            seen.add(key)
            marks.push({ part, commentId, emphasis: getFragmentEmphasis(comment) })
        }
        return marks
    }

    function getFragmentEmphasis(comment) {
        if (!selectedComment) return SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED
        const commentKey = comment?.id ?? comment?.threadId
        const selectedKey = selectedComment?.id ?? selectedComment?.threadId
        if (commentKey === selectedKey) return SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED
        return SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED
    }

    // A comment button stores the draft target (with the user's selected text) in the right-bar
    // reference, then clears the native selection before the composer takes focus. Highlighting that
    // draft here keeps the selected text visible while the user writes; publish/clear resets the
    // reference and the highlight goes with it (replaced by the saved comment's own highlight).
    function getDraftTarget() {
        if (!getCanShowHighlights()) return null
        const draft = rightBarReference[SHARE_LINK_COMMENT_TARGET_REFERENCE]
        if (!draft?.targetText) return null
        // Same staleness guard as the panel composer: ignore a target left over from another schedule.
        if (draft.schedulePath && activeSchedulePath && draft.schedulePath !== activeSchedulePath) return null
        const anchor = { type: draft.type, targetId: draft.targetId, taskIndex: draft.selectionMetadata?.taskIndex }
        if (!isCommentForThisTarget({ anchor })) return null
        return draft
    }

    function getDraftHighlightFragments() {
        if (!draftTarget) return []
        // A part-pinned draft highlights the part's element, not its (often very short) value text.
        if (getShareLinkCommentPartLabel(draftTarget.selectionMetadata?.part)) return []
        return getHighlightFragments(draftTarget.targetText).map(toDraftFragment)
    }

    // No commentId on purpose: there is no saved comment to click through to yet, and
    // resolveClickedCommentId ignores spans without one.
    function toDraftFragment(text) {
        return {
            text,
            commentId: undefined,
            emphasis: SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_DRAFT,
            context: getSelectionMetadataHighlightContext(draftTarget.selectionMetadata),
        }
    }

    function getCommentHighlightContext(comment) {
        return getSelectionMetadataHighlightContext(comment?.anchor)
    }

    function getSelectionMetadataHighlightContext(selectionMetadata = {}) {
        return {
            beforeText: selectionMetadata.beforeText,
            afterText: selectionMetadata.afterText,
        }
    }

    function isCommentForThisTarget(comment) {
        if (targetType === "intro") {
            return isShareLinkCommentForTarget(comment, { targetType: "intro", targetId: "intro" })
        }
        return isShareLinkCommentForTarget(comment, {
            targetType: "task",
            targetId: task?.id,
            taskIndex: getTaskIndex(),
        })
    }

    function getTaskIndex() {
        const parsed = Number.parseInt(task?.$index ?? task?.index ?? 1, 10)
        if (Number.isNaN(parsed) || parsed < 1) return 0
        return parsed - 1
    }

    function scrollToSelectedTarget() {
        if (!isSelected) return undefined
        // When selection is triggered by focusing a reply composer, the browser's focus scroll
        // handling silently cancels a smooth scrollIntoView started in the same beat (observed in
        // Chrome: the call no-ops while an instant scroll works). Deferring slightly lets the
        // focus settle so the smooth scroll survives.
        const timeout = setTimeout(scrollNow, 150)
        return clearScheduledScroll

        function scrollNow() {
            // Always top-align: the comment sits at the top of its target, so centring just adds a big
            // gap above short targets and pushes the top of tall ones (e.g. the intro editor) off
            // screen. scroll-margin-top (set in the Box sx) keeps the target clear of the sticky header.
            ref.current?.scrollIntoView({ behavior: "smooth", block: "start" })
        }

        function clearScheduledScroll() {
            clearTimeout(timeout)
        }
    }

    function applyTextHighlights() {
        const element = ref.current
        if (!element) return undefined
        clearTextHighlights(element)
        clearPartHighlights(element)
        clearMissingHighlight(element)
        if (!hasHighlightTarget) return clearHighlights
        let hasInlineHighlight = false
        for (const fragment of highlightFragments) {
            if (!fragment.text) continue
            hasInlineHighlight =
                highlightText(element, fragment.text, fragment.commentId, fragment.emphasis, fragment.context) ||
                hasInlineHighlight
        }
        for (const mark of partHighlights) {
            hasInlineHighlight = markPartHighlight(element, mark.part, mark) || hasInlineHighlight
        }
        if (!hasInlineHighlight && (highlightFragments.length || partHighlights.length)) {
            element.setAttribute(HIGHLIGHT_MISSING_ATTRIBUTE, "true")
        }
        return clearHighlights

        function clearHighlights() {
            clearTextHighlights(element)
            clearPartHighlights(element)
            clearMissingHighlight(element)
        }
    }

    function handleHighlightClick(event) {
        const clickedCommentId = resolveClickedCommentId(event.target) ?? resolveClickedPartCommentId(event.target)
        if (!clickedCommentId) return
        setSearchParams({ comment: clickedCommentId, ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB })
    }

    // Clicking a marked task item (Title, Duration, Frequency, UoM, Skill, Classification) opens
    // its existing thread or starts a comment pinned to it. This runs in the CAPTURE phase and
    // claims the event because task rows can own clicks themselves (the tailored share view expands
    // a task when its header is clicked) — when comments are enabled, commenting wins. A drag text
    // selection still beats the click: the handler bails while a selection is active, leaving the
    // floating selection button (which captures the part too) to own that flow.
    function handlePartClickCapture(event) {
        if (!canComposeParts) return
        if (window.getSelection?.()?.isCollapsed === false) return
        const partElement = findCommentPartElement(event.target)
        const part = partElement?.getAttribute(SHARE_LINK_COMMENT_PART_ATTRIBUTE)
        if (!getShareLinkCommentPartLabel(part)) return
        event.stopPropagation()
        event.preventDefault()
        const existingCommentId = resolveClickedPartCommentId(event.target)
        if (existingCommentId) {
            setSearchParams({ comment: existingCommentId, ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB })
            return
        }
        composeOnPart(part, partElement)
    }

    // Start a part-pinned comment: capture the displayed value, store the draft target, and open
    // the Comments tab — the same flow as the floating selection button.
    function composeOnPart(part, partElement) {
        const selectedText = getCommentPartValue({ task, part, element: partElement })
        const commentTarget = buildTaskCommentTarget({ task, selectedText, part })
        setReference(
            {
                ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB,
                [SHARE_LINK_COMMENT_TARGET_REFERENCE]: { ...commentTarget, schedulePath },
            },
            "tab-ScheduleRightBar-"
        )
        setSearchParams({ ScheduleRightBarTab: SHARE_LINK_COMMENTS_TAB })
    }

    // Raising the shared focus event from a highlight hover lets the matching panel card light up.
    // Debounced via raiseOnce so rapid pointer moves across a multi-fragment highlight don't thrash.
    function handleHighlightHover(event) {
        const hoveredCommentId = resolveClickedCommentId(event.target) ?? resolveClickedPartCommentId(event.target)
        if (!hoveredCommentId) return
        ShareLinkCommentFocus.raiseOnce(hoveredCommentId)
    }

    function handleHighlightHoverOut(event) {
        if (!resolveClickedCommentId(event.target) && !resolveClickedPartCommentId(event.target)) return
        // Clear must raise immediately — raiseOnce dedupes by event name within its window, so a
        // fast enter/leave would drop the trailing null and leave the focus visually stuck
        ShareLinkCommentFocus.raise(null)
    }

    // Toggle `data-highlight-focused` on the spans owned by the focused comment so the CSS lifts them
    // to the selected tint. Pure attribute toggling — no React re-render.
    function syncHighlightFocus(focusedCommentId) {
        const element = ref.current
        if (!element) return
        const spans = element.querySelectorAll(
            `.${SHARE_LINK_COMMENT_HIGHLIGHT_CLASS}, [${SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE}]`
        )
        for (const span of spans) {
            const ownerId = span.dataset.commentId ?? span.getAttribute(SHARE_LINK_COMMENT_PART_COMMENT_ATTRIBUTE)
            if (focusedCommentId && ownerId === focusedCommentId) {
                span.dataset.highlightFocused = "true"
            } else {
                delete span.dataset.highlightFocused
            }
        }
    }

    function getDataCy() {
        if (isSelected) return `share-link-selected-comment-${targetType}`
        if (hasTargetComment) return `share-link-commented-${targetType}`
        return `share-link-comment-target-${targetType}`
    }

    function getSx() {
        const highlightSx = hasHighlightTarget ? getTextHighlightContainerSx(getContainerEmphasis()) : undefined
        const affordanceSx = canComposeParts ? getPartAffordanceSx() : undefined
        if (!highlightSx && !affordanceSx) return undefined
        return { ...affordanceSx, ...highlightSx }
    }

    // In a comment-enabled share context every marked task item (Title, Duration, Frequency, UoM,
    // Skill, Classification) is a click/tap comment target. The affordance is a hover state only —
    // a tint plus a primary ring so the item clearly reads as selectable — with no extra icons in
    // the row.
    function getPartAffordanceSx() {
        const clickableParts = `& [${SHARE_LINK_COMMENT_PART_ATTRIBUTE}]:not([${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}])`
        return {
            [clickableParts]: {
                position: "relative",
                zIndex: 0,
                cursor: "pointer",
                borderRadius: "4px",
                transition: "background-color 0.15s ease",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    ...getShareLinkPartHighlightSx(),
                    zIndex: -1,
                    pointerEvents: "none",
                    borderRadius: "4px",
                    backgroundColor: "transparent",
                    boxShadow: "none",
                    transition: "background-color 0.15s ease, box-shadow 0.15s ease",
                },
            },
            ...getBoxedPartPseudoSx({ highlighted: false }),
            [`${clickableParts}:hover::before`]: {
                backgroundColor: theme.palette.action.hover,
                boxShadow: `inset 0 0 0 1px ${getHighlightBaseColor(SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED)}`,
            },
        }
    }

    function getContainerEmphasis() {
        if (isSelected) return SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED
        if (selectedComment) return SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED
        return SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED
    }

    function getTextHighlightContainerSx(emphasis) {
        const selectedColor = getHighlightColor(HIGHLIGHT_ALPHA_SELECTED)
        const mutedColor = getHighlightColor(HIGHLIGHT_ALPHA_MUTED)
        const missingAlpha =
            emphasis === SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED ? HIGHLIGHT_ALPHA_MUTED : HIGHLIGHT_ALPHA_SELECTED
        return {
            [`&[${HIGHLIGHT_MISSING_ATTRIBUTE}="true"]`]: {
                backgroundColor: getHighlightColor(missingAlpha),
                borderLeft: "3px solid",
                borderColor: getHighlightBaseColor(emphasis),
                borderRadius: 1,
                boxShadow: `inset 0 0 0 1px ${getHighlightBaseColor(emphasis)}`,
                pl: 1,
                ml: -1,
            },
            "& .share-link-comment-text-highlight": {
                backgroundColor: selectedColor,
                borderRadius: "4px",
                padding: "2px 0",
                margin: 0,
                cursor: "pointer",
                boxDecorationBreak: "clone",
                WebkitBoxDecorationBreak: "clone",
            },
            [`& .share-link-comment-text-highlight[data-highlight-emphasis='${SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED}']`]:
                {
                    backgroundColor: mutedColor,
                },
            // A draft (comment being composed) reads like a focused highlight so the author can see
            // exactly what they selected while typing, but with a text cursor — it isn't clickable.
            [`& .share-link-comment-text-highlight[data-highlight-emphasis='${SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_DRAFT}']`]:
                {
                    backgroundColor: selectedColor,
                    boxShadow: "none",
                    cursor: "text",
                },
            // Hover linkage from the panel: a focused highlight reads as "selected", even when it
            // would otherwise be muted, without adding an extra edge outline.
            '& .share-link-comment-text-highlight[data-highlight-focused="true"]': {
                backgroundColor: selectedColor,
                boxShadow: "none",
            },
            // Part-pinned comments mark the task item's ELEMENT (Duration, Frequency, …) — same
            // emphasis language as the text spans, applied via attribute instead of a wrapping span.
            [`& [${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}]`]: {
                position: "relative",
                zIndex: 0,
                backgroundColor: "transparent",
                borderRadius: "4px",
                cursor: "pointer",
                boxShadow: "none",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    ...getShareLinkPartHighlightSx(),
                    zIndex: -1,
                    pointerEvents: "none",
                    borderRadius: "4px",
                    backgroundColor: selectedColor,
                    boxShadow: `inset 0 0 0 1px ${getHighlightBaseColor(
                        SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED
                    )}`,
                },
            },
            ...getBoxedPartPseudoSx({ highlighted: true }),
            [`& [${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}='${SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED}']::before`]:
                {
                    backgroundColor: mutedColor,
                    boxShadow: `inset 0 0 0 1px ${getHighlightBaseColor(SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED)}`,
                },
            [`& [${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}='${SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_DRAFT}']::before`]:
                {
                    backgroundColor: selectedColor,
                    boxShadow: "none",
                },
            [`& [${SHARE_LINK_COMMENT_PART_HIGHLIGHT_ATTRIBUTE}][data-highlight-focused="true"]::before`]: {
                backgroundColor: selectedColor,
                boxShadow: "none",
            },
        }
    }

    function clearMissingHighlight(element) {
        element.removeAttribute(HIGHLIGHT_MISSING_ATTRIBUTE)
    }

    // Text highlights are always a translucent primary tint so editor selection markers read as
    // schedule-focused affordances rather than party/status chips.
    function getHighlightColor(alpha) {
        const baseColor = getHighlightBaseColor(SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED)
        const [r, g, b] = decomposeColor(baseColor).values
        const [br, bg, bb] = decomposeColor(theme.palette.background.paper ?? "#fff").values
        return `rgb(${Math.round(br + alpha * (r - br))}, ${Math.round(bg + alpha * (g - bg))}, ${Math.round(
            bb + alpha * (b - bb)
        )})`
    }

    function getHighlightBaseColor(emphasis = SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_SELECTED) {
        if (emphasis === SHARE_LINK_COMMENT_HIGHLIGHT_EMPHASIS_MUTED) {
            return theme.palette.text.disabled
        }
        return theme.palette.primary.main
    }

    function getHighlightPartyComment() {
        if (selectedComment && isCommentForThisTarget(selectedComment)) return selectedComment
        if (targetComments.length === 1) return targetComments[0]
        return null
    }

    // The not-found marker follows the same party colour as the inline highlight would:
    // "warning" (amber) for share-link comments, "primary" (teal) for internal ones.
    function getMarkerColorKey() {
        return getHighlightPartyColorKey(getHighlightPartyComment())
    }

    function getMarkerSx() {
        const colorKey = getMarkerColorKey()
        const markerSx = {
            position: "relative",
            bgcolor: `${colorKey}.lighter`,
            borderLeft: "3px solid",
            borderColor: `${colorKey}.main`,
            borderRadius: 1,
            minHeight: 6,
            mt: 1,
        }
        if (!isSelected) return markerSx
        return {
            ...markerSx,
            outline: "1px solid",
            outlineColor: `${colorKey}.main`,
        }
    }

    function getMarkerCountSx() {
        const colorKey = getMarkerColorKey()
        return {
            position: "absolute",
            top: -8,
            right: -8,
            minWidth: 18,
            height: 18,
            px: 0.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: "9px",
            bgcolor: `${colorKey}.main`,
            color: `${colorKey}.contrastText`,
        }
    }
}
